import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";

interface SearchItem {
    id: number;
    name: string;
    model: string;
}

interface SearchGroup {
    model: string;
    label: string;
    color: number;
    has_more: boolean;
    items: SearchItem[];
}

interface SearchPayload {
    groups: SearchGroup[];
    min_query_length: number;
}

const DEBOUNCE_MS = 250;

export function PmGlobalSearch({ onOpenRecord }: {
    onOpenRecord?(model: string, id: number): void;
}) {
    const { t } = useTranslation();
    const [term, setTerm] = useState("");
    const [typed, setTyped] = useState("");
    const [open, setOpen] = useState(false);
    const holder = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => setTerm(typed.trim()), DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [typed]);

    useEffect(() => {
        function onAway(event: MouseEvent) {
            if (holder.current && !holder.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        function onKey(event: KeyboardEvent) {
            if (event.key === "Escape") {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onAway);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onAway);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const results = useQuery<{ data: SearchPayload }>({
        queryKey: ["global-search", term],
        enabled: term.length >= 2,
        staleTime: 30_000,
        queryFn: () => api.raw<SearchPayload>(`/search?q=${encodeURIComponent(term)}`),
    });

    const groups = results.data?.data.groups ?? [];
    const searching = term.length >= 2;
    const empty = searching && !results.isLoading && !groups.length;

    function pick(item: SearchItem) {
        setOpen(false);
        setTyped("");
        onOpenRecord?.(item.model, item.id);
    }

    return (
        <div className="pm-global-search" ref={holder}>
            <span className="pm-global-search-icon"><i className="fa fa-search" /></span>
            <input className="pm-global-search-input" value={typed}
                   placeholder={t("search.placeholder")}
                   onChange={(event) => { setTyped(event.target.value); setOpen(true); }}
                   onFocus={() => setOpen(true)} />
            {typed && (
                <button className="pm-global-search-clear" aria-label={t("search.clear")}
                        onClick={() => { setTyped(""); setOpen(false); }}>
                    <i className="fa fa-times" />
                </button>
            )}
            {open && searching && (
                <div className="pm-global-search-panel">
                    {results.isLoading ? (
                        <div className="pm-global-search-note">{t("search.searching")}</div>
                    ) : empty ? (
                        <div className="pm-global-search-note">{t("search.nothing")}</div>
                    ) : groups.map((group) => (
                        <div key={group.model} className="pm-global-search-group">
                            <div className="pm-global-search-group-head">
                                <span>{group.label}</span>
                                {group.has_more && (
                                    <span className="pm-global-search-more">{t("search.more")}</span>
                                )}
                            </div>
                            {group.items.map((item) => (
                                <button key={`${item.model}-${item.id}`}
                                        className="pm-global-search-item"
                                        onClick={() => pick(item)}>
                                    {item.name}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
